export const TARGET_AUDIENCE_OPTION_IDS = [
  'consumers',
  'business_customers',
  'public_authorities',
  'investors_financial_institutions',
  'internal_management',
  'other',
] as const;

export type TargetAudienceOptionId =
  (typeof TARGET_AUDIENCE_OPTION_IDS)[number];

export type TargetAudienceValues = {
  audience: TargetAudienceOptionId[];
  details: string;
};

export type TargetAudienceLanguage = 'en' | 'pl' | 'sv';

type TargetAudienceSerializationOptions = {
  language?: string;
  audienceSectionLabel?: string;
  detailsSectionLabel?: string;
};

const TARGET_AUDIENCE_OPTION_LABELS: Record<
  TargetAudienceLanguage,
  Record<TargetAudienceOptionId, string>
> = {
  en: {
    consumers: 'Consumers',
    business_customers: 'Business customers',
    public_authorities: 'Public authorities',
    investors_financial_institutions: 'Investors and financial institutions',
    internal_management: 'Internal management',
    other: 'Other',
  },
  pl: {
    consumers: 'Konsumenci',
    business_customers: 'Klienci biznesowi',
    public_authorities: 'Organy publiczne',
    investors_financial_institutions: 'Inwestorzy i instytucje finansowe',
    internal_management: 'Zarządzanie wewnętrzne',
    other: 'Inne',
  },
  sv: {
    consumers: 'Konsumenter',
    business_customers: 'Företagskunder',
    public_authorities: 'Offentliga myndigheter',
    investors_financial_institutions: 'Investerare och finansinstitut',
    internal_management: 'Intern ledning',
    other: 'Annat',
  },
};

const TARGET_AUDIENCE_SECTION_LABELS: Record<
  TargetAudienceLanguage,
  { audience: string; details: string }
> = {
  en: {
    audience: 'Audience',
    details: 'Details',
  },
  pl: {
    audience: 'Odbiorca docelowy',
    details: 'Szczegóły',
  },
  sv: {
    audience: 'Målgrupp',
    details: 'Detaljer',
  },
};

const normalizeLookupValue = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const normalizeLanguage = (language?: string): TargetAudienceLanguage => {
  const normalizedLanguage = (language || 'en').toLowerCase();

  if (normalizedLanguage.startsWith('pl')) {
    return 'pl';
  }

  if (normalizedLanguage.startsWith('sv')) {
    return 'sv';
  }

  return 'en';
};

const LABEL_ALIASES: Record<string, TargetAudienceOptionId> = Object.entries(
  TARGET_AUDIENCE_OPTION_LABELS
).reduce<Record<string, TargetAudienceOptionId>>((acc, [, labels]) => {
  Object.entries(labels).forEach(([optionId, label]) => {
    acc[normalizeLookupValue(label)] = optionId as TargetAudienceOptionId;
  });
  return acc;
}, {});

const AUDIENCE_SECTION_KEYS = [
  'audience',
  'category',
  'odbiorca',
  'kategoria',
  'malgrupp',
  'målgrupp',
  'kategori',
].map(normalizeLookupValue);

const DETAILS_SECTION_KEYS = [
  'details',
  'other',
  'szczegoly',
  'szczegóły',
  'detale',
  'inne',
  'detaljer',
  'ovrigt',
  'övrigt',
  'annat',
].map(normalizeLookupValue);

const EMPTY_TARGET_AUDIENCE: TargetAudienceValues = {
  audience: [],
  details: '',
};

const normalizeAudienceToken = (
  token: string
): TargetAudienceOptionId | null => {
  const trimmedToken = token.trim();
  if (!trimmedToken) {
    return null;
  }

  if (
    TARGET_AUDIENCE_OPTION_IDS.includes(trimmedToken as TargetAudienceOptionId)
  ) {
    return trimmedToken as TargetAudienceOptionId;
  }

  return LABEL_ALIASES[normalizeLookupValue(trimmedToken)] || null;
};

const parseAudienceList = (value: string): TargetAudienceOptionId[] => {
  const normalizedValues = value
    .split(',')
    .map(normalizeAudienceToken)
    .filter((optionId): optionId is TargetAudienceOptionId => !!optionId);

  return [...new Set(normalizedValues)];
};

export const parseTargetAudience = (rawValue: string): TargetAudienceValues => {
  if (!rawValue) {
    return EMPTY_TARGET_AUDIENCE;
  }

  const parsedValue: TargetAudienceValues = {
    audience: [],
    details: '',
  };

  const sections = rawValue
    .split('|')
    .map((section) => section.trim())
    .filter(Boolean);

  for (const section of sections) {
    const [left, ...rightParts] = section.split(':');
    const right = rightParts.join(':').trim();
    const key = normalizeLookupValue(left);

    if (AUDIENCE_SECTION_KEYS.some((keyword) => key.includes(keyword))) {
      parsedValue.audience = parseAudienceList(right);
      continue;
    }

    if (DETAILS_SECTION_KEYS.some((keyword) => key.includes(keyword))) {
      parsedValue.details = right;
    }
  }

  if (!parsedValue.audience.length && !parsedValue.details) {
    const fallbackAudience = parseAudienceList(rawValue);
    if (fallbackAudience.length) {
      parsedValue.audience = fallbackAudience;
    } else {
      parsedValue.details = rawValue;
    }
  }

  return parsedValue;
};

export const toTargetAudience = (
  values: TargetAudienceValues,
  options: TargetAudienceSerializationOptions = {}
) => {
  const language = normalizeLanguage(options.language);
  const audienceSectionLabel =
    options.audienceSectionLabel ||
    TARGET_AUDIENCE_SECTION_LABELS[language].audience;
  const detailsSectionLabel =
    options.detailsSectionLabel ||
    TARGET_AUDIENCE_SECTION_LABELS[language].details;

  return [
    values.audience.length &&
      `${audienceSectionLabel}: ${values.audience.join(',')}`,
    values.details && `${detailsSectionLabel}: ${values.details}`,
  ]
    .filter(Boolean)
    .join(' | ');
};

export const getTargetAudienceLabel = (
  optionId: TargetAudienceOptionId,
  language: string = 'en'
) => TARGET_AUDIENCE_OPTION_LABELS[normalizeLanguage(language)][optionId];

export const formatTargetAudience = (
  rawValue: string,
  getLabel: (
    _optionId: TargetAudienceOptionId
  ) => string = getTargetAudienceLabel
) => {
  const parsedValue = parseTargetAudience(rawValue);
  const audienceLabel = parsedValue.audience.map(getLabel).join(', ');

  return [audienceLabel, parsedValue.details].filter(Boolean).join(' | ');
};
